import React, { useEffect, useState } from 'react';

import { useFormik } from 'formik';
import { validation, formValues } from './validationSchema';

import { useDispatch, useSelector } from 'react-redux';
import { editWaste, fetchClients, showWasteById } from '../../../store/actions/waste';
import { fetchWarehouses } from '../../../store/actions/warehouse';
import { fetchContainers } from '../../../store/actions/container';

import { errorHelper, Loader } from '../../../utils/tools';
import { loadingCodes } from '../../../utils/constants/loadingCodes';
import { acronyms } from '../../../utils/constants/acronyms';

import { Form, Row, Col, Button, InputGroup } from 'react-bootstrap';

import CustomModalLarge from '../../../hoc/customModalLarge';
import AddClient from '../../client/add';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { sk } from 'date-fns/locale';

const UpdateWaste = ({wasteToEdit, onClose}) => {
    
    const today = new Date(); // Current date

    const currentEvidenceList = useSelector(state => state.evidenceList.current);
    const currentEvidenceListId = currentEvidenceList?._id;
    const clients = useSelector(state => state.waste.clients);
    const currentWaste = useSelector(state => state.waste.currentWaste);
    const currentWasteLoading = useSelector(state => state.waste.currentWaste.loading);
    const currentBranch = useSelector(state => state.branch.current);
    const warehouses = useSelector(state => state.branch.warehouse);
    const containers = useSelector(state => state.branch.container);

    const wasteId = wasteToEdit;

    const dispatch = useDispatch();
    
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: formValues,
        validationSchema: validation,
        onSubmit: (values) => {
            handleSubmit(values);
        }

    });


    const [isAddClientModalVisible, setIsAddClientModalVisible] = useState(false);

    // Toggle income and disposal activity
    const [isIncomeActive, setIsIncomeActive] = useState(true);
    const [isDisposalActive, setIsDisposalActive] = useState(true);

    const handleIncomeChange = (event) => {
        const raw = event.target.value;
        if (raw === '') {
            formik.setFieldValue('income', '');
            return;
        }
        const inputValue = parseFloat(raw);
        if (isNaN(inputValue)) return;
        formik.setFieldValue('income', inputValue);
        if (inputValue !== 0) {
            setIsDisposalActive(false);
            formik.setFieldValue('disposal', 0);
        } else {
            setIsDisposalActive(true);
        }
    };

    const handleDisposalChange = (event) => {
        const raw = event.target.value;
        if (raw === '') {
            formik.setFieldValue('disposal', '');
            return;
        }
        const inputValue = parseFloat(raw);
        if (isNaN(inputValue)) return;
        formik.setFieldValue('disposal', inputValue);
        if (inputValue !== 0) {
            setIsIncomeActive(false);
            formik.setFieldValue('income', 0);
        } else {
            setIsIncomeActive(true);
        }
    };

    // Fetch user's clients, warehouses and containers
    useEffect(() => {
        dispatch(fetchClients('WASTE'));
        if(currentBranch !== '') {
            dispatch(fetchWarehouses(currentBranch));
            dispatch(fetchContainers(currentBranch));
        }

    }, [dispatch, currentBranch]);

    // Fetch waste by Id
    useEffect(() => {
        if (currentEvidenceListId && currentBranch && wasteId) {
            dispatch(showWasteById({
                evidenceList: currentEvidenceListId,
                branchId: currentBranch,
                wasteId: wasteId
            }));
        }
    }, [dispatch]);

    useEffect(() => {
        if (!isAddClientModalVisible) return;
        const stopEsc = (e) => {
            if (e.key === 'Escape') {
                e.stopImmediatePropagation();
                setIsAddClientModalVisible(false);
            }
        };
        document.addEventListener('keydown', stopEsc, true);
        return () => document.removeEventListener('keydown', stopEsc, true);
    }, [isAddClientModalVisible]);

    const handleClientCreated = async (clientId) => {
        await dispatch(fetchClients('WASTE'));
        formik.setFieldValue('client', clientId);
        formik.setFieldValue('clientBranchId', '');
        setIsAddClientModalVisible(false);
    };

    const handleSubmit = (values) => {
        values.currentEvidenceListId = currentEvidenceListId;
        values.wasteId = wasteId;
        if (currentBranch !== '')
            values.branchId = currentBranch;
        dispatch(editWaste(values));
        onClose();
    };

    useEffect(() => {
        if (currentWaste.client) {
            formik.setValues({
                ...formik.values,
                date: currentWaste.date,
                warehouse:  currentWaste.warehouse,
                container: currentWaste.container,
                income: currentWaste.income,
                disposal: currentWaste.disposal,
                loadingCode: currentWaste.loadingCode,
                client: currentWaste.client._id,
                clientBranchId: currentWaste.clientBranchId || '',
                note: currentWaste.note,
                acronym: currentWaste.acronym
            });
            setIsIncomeActive(!(currentWaste.disposal > 0));
            setIsDisposalActive(!(currentWaste.income > 0));
        }
    }, [currentWaste.client]);

    return (
        <>
        <Form
            component="form"
            id="editWasteForm"
            onSubmit={formik.handleSubmit}>

                <h2>
                    {isIncomeActive && !isDisposalActive
                        ? 'Upraviť vznik/príjem'
                        : !isIncomeActive && isDisposalActive
                        ? 'Upraviť nakladanie'
                        : 'Upraviť odpad'}
                </h2>
                <hr />

                {currentWasteLoading && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '75vh' }}>
                        <Loader />
                    </div>
                )}
                {!currentWasteLoading && (
                <>
                <Row>
                    {/* Date */}
                    <Form.Group className="mb-3" controlId="date">
                        <Form.Label>Dátum*</Form.Label>
                        <div>
                            <DatePicker
                                id="date"
                                name="date"
                                showIcon
                                toggleCalendarOnIconClick
                                maxDate={today}
                                selected={formik.values.date}
                                onChange={(date) => formik.setFieldValue('date', date)}
                                dateFormat="dd.MM.yyyy" // Príklad formátu dátumu, môžete prispôsobiť podľa potreby
                                className={`form-control ${errorHelper(formik, 'date').error ? 'is-invalid' : ''}`}
                                locale={sk}
                                />
                        </div>
                        <Form.Control.Feedback type="invalid">
                            {errorHelper(formik, 'date').helperText} {/* Zobrazenie chybovej správy podľa helperText hodnoty z errorHelper */}
                        </Form.Control.Feedback>
                    </Form.Group>

                    {/* Warehouse */}
                    <Form.Group className="mb-3" controlId="formBasicWarehouse">
                        <Form.Label>Sklad</Form.Label>
                        <Form.Select
                            aria-label="Sklad:"
                            {...formik.getFieldProps('warehouse')}
                            isInvalid={errorHelper(formik, 'warehouse').error}
                        >
                        <option value="" disabled>
                            Vyberte sklad:
                        </option>

                        {currentWaste.warehouse ? (
                            <option value={currentWaste.warehouse}>
                                {currentWaste.warehouse}
                            </option>
                        ) : null}

                        { warehouses.map((warehouse, index) => (
                            <option key={index} value={warehouse}>
                                {warehouse}
                            </option>
                        ))}

                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errorHelper(formik, 'company').helperText}
                        </Form.Control.Feedback>
                    </Form.Group>

                    {/* Container */}
                    <Form.Group className="mb-3" controlId="formBasicContainer">
                        <Form.Label>Nádoba</Form.Label>
                        <Form.Select
                            aria-label="Nádoba:"
                            {...formik.getFieldProps('container')}
                            isInvalid={errorHelper(formik, 'container').error}
                        >
                        <option value="" disabled>
                            Vyberte nádobu:
                        </option>

                        {currentWaste.container ? (
                            <option value={currentWaste.container}>
                                {currentWaste.container}
                            </option>
                        ) : null}

                        { containers.map((container, index) => (
                            <option key={index} value={container}>
                                {container}
                            </option>
                        ))}

                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errorHelper(formik, 'container').helperText} {/* Zobrazenie chybovej správy podľa helperText hodnoty z errorHelper */}
                        </Form.Control.Feedback>
                    </Form.Group>

                    {/* Income */}
                    {isIncomeActive && (
                        <Form.Group className="mb-3" controlId="formBasicIncome">
                            <Form.Label>
                                {isIncomeActive && isDisposalActive
                                    ? 'Vznik/Príjem - Hmotnosť (t)*'
                                    : 'Hmotnosť (t)*'}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                placeholder="Vznik/Príjem:"
                                {...formik.getFieldProps('income')}
                                isInvalid={errorHelper(formik, 'income').error}
                                onChange={handleIncomeChange}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errorHelper(formik, 'income').helperText}
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}

                    {/* Disposal */}
                    {isDisposalActive && (
                        <Form.Group className="mb-3" controlId="formBasicDisposal">
                            <Form.Label>
                                {isIncomeActive && isDisposalActive
                                    ? 'Nakladanie - Hmotnosť (t)*'
                                    : 'Hmotnosť (t)*'}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                placeholder="Nakladanie:"
                                {...formik.getFieldProps('disposal')}
                                isInvalid={errorHelper(formik, 'disposal').error}
                                onChange={handleDisposalChange}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errorHelper(formik, 'disposal').helperText}
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}

                    {/* Loading Code */}
                    <Form.Group className="mb-3" controlId="formBasicLoadingCode">
                        <Form.Label>Kód nakladania*</Form.Label>
                        <Form.Select
                            aria-label="Vyberte kód nakladania:"
                            {...formik.getFieldProps("loadingCode")}
                            isInvalid={errorHelper(formik, "loadingCode").error}
                        >
                            <option value="" disabled>
                                Vyberte kód nakladania:
                            </option>

                            {Object.entries(loadingCodes).map(([category, codes]) => {
                                const filtered = currentEvidenceList?.activityCode === 'R' && currentEvidenceList?.primaryLoadingCode
                                    ? codes.filter(c => {
                                        const isRestrictedR = /^R([1-9]|10|11)$/.test(c.value);
                                        return !isRestrictedR || c.value === currentEvidenceList.primaryLoadingCode;
                                    })
                                    : codes;
                                if (filtered.length === 0) return null;
                                return (
                                    <optgroup key={category} label={category}>
                                        {filtered.map((loadingCode) => (
                                            <option key={loadingCode.value} value={loadingCode.value}>
                                                {loadingCode.value} - {loadingCode.label}
                                            </option>
                                        ))}
                                    </optgroup>
                                );
                            })}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errorHelper(formik, "loadingCode").helperText}
                        </Form.Control.Feedback>
                    </Form.Group>

                    {/* Client */}
                    <Form.Group className="mb-3" controlId="formBasicClient">
                        <Form.Label>Odberateľ/Dodávateľ*</Form.Label>
                        <InputGroup hasValidation>
                            <Form.Select
                                aria-label="Odberateľ/Dodávateľ:"
                                {...formik.getFieldProps('client')}
                                isInvalid={errorHelper(formik, 'client').error}
                                onChange={e => {
                                    formik.setFieldValue('client', e.target.value);
                                    formik.setFieldValue('clientBranchId', '');
                                }}
                            >
                                <option value="" disabled>
                                    Vyberte dodávateľa/odberateľa:
                                </option>

                                {currentWaste.client ? (
                                    <option value={currentWaste.client._id}>
                                        {currentWaste.client.companyName}
                                    </option>
                                ) : null}

                                {clients && clients.length > 0 ? (
                                    clients.map((client) =>
                                        client._id === currentWaste.client?._id ? null : (
                                            <option key={client._id} value={client._id}>
                                                {client.companyName}
                                            </option>
                                        )
                                    )
                                ) : null}
                            </Form.Select>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setIsAddClientModalVisible(true)}
                                title="Pridať nového odberateľa/dodávateľa"
                            >
                                + Pridať
                            </Button>
                            <Form.Control.Feedback type="invalid">
                                {errorHelper(formik, 'client').helperText}
                            </Form.Control.Feedback>
                        </InputGroup>
                    </Form.Group>

                    {/* Branch */}
                    {(() => {
                        const selectedClient = clients.find(c => c._id === formik.values.client);
                        const branches = selectedClient?.branches?.filter(b => b.active) || [];
                        if (branches.length === 0) return null;
                        return (
                            <Form.Group className="mb-3" controlId="formBasicClientBranch">
                                <Form.Label>Prevádzkareň</Form.Label>
                                <Form.Select
                                    aria-label="Prevádzkareň:"
                                    {...formik.getFieldProps('clientBranchId')}
                                >
                                    <option value="">Sídlo (hlavná adresa)</option>
                                    {branches.map(branch => (
                                        <option key={branch._id} value={branch._id}>
                                            {branch.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        );
                    })()}

                    {/* Note */}
                    <Form.Group className="mb-3" controlId="formBasicNote">
                        <Form.Label>Poznámka</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Poznámka:"
                            {...formik.getFieldProps('note')}
                            isInvalid={errorHelper(formik, 'note').error} // Priraď isInvalid na základe error hodnoty z errorHelper
                        />
                        <Form.Control.Feedback type="invalid">
                            {errorHelper(formik, 'note').helperText} {/* Zobrazenie chybovej správy podľa helperText hodnoty z errorHelper */}
                        </Form.Control.Feedback>
                    </Form.Group>

                    {/* Acronym */}
                    <Form.Group className="mb-3" controlId="formBasicAcronym">
                        <Form.Label>Skratka</Form.Label>
                        <Form.Select
                            aria-label="Vyberte skratku:"
                            {...formik.getFieldProps('acronym')}
                            isInvalid={errorHelper(formik, 'acronym').error}
                        >
                            <option value="" disabled>
                                Vyberte skratku:
                            </option>

                            {acronyms.map((acronym) => (
                                    <option key={acronym.value} value={acronym.value}>
                                        {acronym.label}
                                    </option>
                                ))}

                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {errorHelper(formik, 'acronym').helperText}
                        </Form.Control.Feedback>
                    </Form.Group>

                </Row>

                <Row className="mt-3">
                    <hr />
                    <Col className="d-flex justify-content-end">
                        <Button
                            className='button-custom-large'
                            variant='primary'
                            type="submit"
                            size="lg"
                        >
                            Upraviť odpad
                        </Button>
                    </Col>
                </Row>
                </>
                )}

        </Form>

        <CustomModalLarge
            isVisible={isAddClientModalVisible}
            onClose={() => setIsAddClientModalVisible(false)}
        >
            <AddClient
                isVisible={isAddClientModalVisible}
                onClose={() => setIsAddClientModalVisible(false)}
                onSuccess={handleClientCreated}
                initialCategories={['WASTE']}
            />
        </CustomModalLarge>
        </>
    );
};

export default UpdateWaste;
